import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Hidden,
  IconButton,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DatePicker from "../reusable/DatePicker";
import SelectItemFromList from "../reusable/SelectItemFromList";
import api from "../../pages/api/base-url";
import { LinkToShare, Version } from "./apps-types";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import { useTheme } from "@mui/material/styles";
import AddMinutesToDate from "../../utils/addMinutesToDate";
import LinkSummaryDialog from "./LinkGenerationSummary";
import { mutate } from "swr";
import AddLinkIcon from "@mui/icons-material/AddLink";

export default function LinkGenerationDialog(props: {
  name: string;
  versions: Version[];
  platform: string;
  isVersionTable?: boolean;
  currentAndroidVersion?: string;
  setCurrentAndroidVersion?: any;
  currentIosVersion?: string;
  setCurrentIosVersion?: any;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  /* General state variables to manage all the components in the dialog */
  const [openDialog, setOpen] = useState(false);

  /* State variables to select platform(s) and version(s) */
  const [androidVersion, setAndroidVersion] = useState<string>(
    props.currentAndroidVersion !== undefined &&
      props.currentAndroidVersion !== null
      ? props.currentAndroidVersion
      : ""
  );
  const [iOSVersion, setiOSVersion] = useState<string>(
    props.currentIosVersion !== undefined && props.currentIosVersion !== null
      ? props.currentIosVersion
      : ""
  );

  const title = t("Share");

  /* State variables to customize link features (starting time, duration and authentication) */
  const [startingTime, setStartingTime] = useState<Date | null>(
    new Date(Date.now())
  );

  const [endingTime, setEndingTime] = useState<Date | null>(
    () => AddMinutesToDate(new Date(Date.now()), 100) ?? null
  );

  const [duration, setDuration] = useState("100");
  const [durationError, setDurationError] = useState(false);

  const [maxDownloads, setMaxDownloads] = useState("10");
  const [maxDownloadsError, setMaxDownloadsError] = useState(false);

  const [auth, setAuth] = useState(true);

  const [openGeneratedLinkSummary, setOpenGeneratedLinkSummary] =
    useState(false);
  const [generatedLinkText, setGeneratedLinkText] = useState("");
  const [generatedLink, setGeneratedLink] = useState<LinkToShare | null>();

  const [checked, setChecked] = useState(false);

  const resetAll = () => {
    setiOSVersion("");
    setAndroidVersion("");
    setStartingTime(new Date(Date.now()));
    setDuration("100");
    setDurationError(false);
    setMaxDownloads("10");
    setMaxDownloadsError(false);
    setAuth(true);
    setOpenGeneratedLinkSummary(false);
    setGeneratedLinkText("");
    props.currentAndroidVersion !== undefined &&
      props.currentAndroidVersion !== null &&
      props.setCurrentAndroidVersion();
    props.currentIosVersion !== undefined &&
      props.currentIosVersion !== null &&
      props.setCurrentIosVersion();
    setGeneratedLink(null);
  };

  const isConfirmButtonEnabled =
    (androidVersion !== "" || iOSVersion !== "") &&
    startingTime !== null &&
    duration !== "" &&
    maxDownloads !== "" &&
    (auth || !auth);

  const handleOpenDialog = () => {
    setOpen(true);

    if (props.currentAndroidVersion !== undefined) {
      setAndroidVersion(props.currentAndroidVersion);
    }
    props.currentIosVersion !== undefined &&
      setiOSVersion(props.currentIosVersion);
  };

  const handleCloseDialog = () => {
    resetAll();
    setOpen(false);
  };

  const handleOpenSummaryDialog = () => {
    setOpenGeneratedLinkSummary(true);
  };

  const handleSelectAndroidVersion = (event: SelectChangeEvent) => {
    setAndroidVersion(event.target.value as string);
  };

  const handleSelectiOSVersion = (event: SelectChangeEvent) => {
    setiOSVersion(event.target.value as string);
  };

  const handleStartingTime = (newValue: Date | null) => {
    setStartingTime(newValue);
  };

  const handleEndingTime = (newValue: Date | null) => {
    setEndingTime(newValue);
  };

  const handleMaximumDownloads = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setMaxDownloads(event.target.value);

    if (event.target.value !== "") {
      setMaxDownloadsError(false);
    } else {
      setMaxDownloadsError(true);
    }
  };

  const handleAuthentication = (
    event: React.MouseEvent<HTMLElement>,
    selectedAuth: boolean | null
  ) => {
    if (selectedAuth !== null) {
      setAuth(selectedAuth);
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  useEffect(() => {
    setEndingTime(AddMinutesToDate(new Date(Date.now()), 100));
  }, []);

  async function createLink(
    validity_start: string,
    validity_end: string,
    versionIDs: number[],
    authenticated?: boolean,
    maxDownloads?: number
  ) {
    let payload = {
      validity_start: validity_start,
      validity_end: validity_end,
      versionIDs: versionIDs,
      isAuthenticated: authenticated,
      maxDownloads: maxDownloads,
    };

    let res = await api.post("/links", payload);

    if (res.status >= 200 && res.status < 300) {
      mutate(`/links`);
      mutate(`/links?onlyActive=false`);

      const baseUrl = window.location.protocol + "//" + window.location.host;

      let response = await api.get(
        auth ? `/links/auth/${res.data.uuid}` : `/links/${res.data.uuid}`
      );
      setGeneratedLink(response.data);
      if (auth) {
        setGeneratedLinkText(
          (
            baseUrl + `/${props.name}/download/auth/${res.data.uuid}`
          ).replaceAll(" ", "%20")
        );
      } else {
        setGeneratedLinkText(
          (baseUrl + `/${props.name}/download/${res.data.uuid}`).replaceAll(
            " ",
            "%20"
          )
        );
      }
    }
  }

  const handleSubmit = () => {
    const versionArray = [];
    if (iOSVersion) {
      versionArray.push(Number(iOSVersion));
    }

    if (androidVersion) {
      versionArray.push(Number(androidVersion));
    }

    /*if (checked) {
      const calculatedEndingTime = AddMinutesToDate(
        startingTime,
        Number(duration)
      );
      if (calculatedEndingTime) {
        setEndingTime(calculatedEndingTime);
      }
    }*/

    // if((androidVersion || iOSVersion) && startingTime && duration && maxDownloads) {
    const startingTimeToISO = startingTime?.toISOString();
    if (startingTimeToISO != null && endingTime != null) {
      createLink(
        startingTimeToISO,
        endingTime?.toISOString(),
        versionArray,
        auth,
        Number(maxDownloads)
      );
      //}

      handleCloseDialog();
      handleOpenSummaryDialog();
    }
  };

  return (
    <Box>
      {props.isVersionTable ? (
        <Tooltip title={title}>
          <Button onClick={handleOpenDialog}>
            <ShareIcon color="action" />
          </Button>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          onClick={handleOpenDialog}
          startIcon={<AddLinkIcon />}
        >
          {t("New Link")}
        </Button>
      )}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t("Link Generation")}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            spacing={{ xs: 1, sm: 2 }}
          >
            <Grid item xs={12}>
              <Hidden smDown>
                <Divider sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {props.platform === "Android; iOS;"
                      ? t("Select the versions to be shared.")
                      : t("Select the version to be shared.")}
                  </Typography>
                </Divider>
              </Hidden>
              <Hidden smUp>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", textAlign: "center" }}
                >
                  {props.platform === "Android; iOS;"
                    ? t("Select the versions to be shared.")
                    : t("Select the version to be shared.")}
                </Typography>
              </Hidden>
            </Grid>
            <Grid item xs={12}>
              {/* <Stack spacing={4} sx={{width: "100%"}}> */}
              <Stack spacing={2}>
                {(props.platform === "Android" ||
                  props.platform === "Android; iOS;") && (
                  <SelectItemFromList
                    handleSelection={handleSelectAndroidVersion}
                    label={
                      props.platform === "Android; iOS;"
                        ? t("Android version")
                        : t("Version")
                    }
                    elementList={props.versions.filter(
                      (item: Version) => item.platform === "ANDROID"
                    )}
                    selected={androidVersion}
                  />
                )}
                {(props.platform === "iOS" ||
                  props.platform === "Android; iOS;") && (
                  <SelectItemFromList
                    handleSelection={handleSelectiOSVersion}
                    label={
                      props.platform === "Android; iOS;"
                        ? t("iOS version")
                        : t("Version")
                    }
                    elementList={props.versions.filter(
                      (item: Version) => item.platform === "IOS"
                    )}
                    selected={iOSVersion}
                  />
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Hidden smDown>
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("Select starting date and duration.")}
                  </Typography>
                </Divider>
              </Hidden>
              <Hidden smUp>
                <Divider sx={{ mt: 3, mb: 2 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", textAlign: "center" }}
                >
                  {t("Select starting date and duration.")}
                </Typography>
              </Hidden>
            </Grid>

            <Hidden smUp>
              <Grid item xs={12}>
                <DatePicker
                  handlingTime={handleStartingTime}
                  dateTime={startingTime}
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker
                  handlingTime={handleEndingTime}
                  dateTime={endingTime}
                />
              </Grid>
            </Hidden>
            <Hidden smDown>
              <Stack
                direction="row"
                divider={<Divider orientation="vertical" flexItem />}
                spacing={2}
                alignItems="center"
                justifyContent="center"
                width={"100%"}
                sx={{ ml: 2 }}
              >
                <DatePicker
                  handlingTime={handleStartingTime}
                  dateTime={startingTime}
                />
                <DatePicker
                  handlingTime={handleEndingTime}
                  dateTime={endingTime}
                />
              </Stack>
            </Hidden>
            <Grid item xs={12}>
              <Hidden smDown>
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t(
                      "Select maximum amount of downloads and authentication."
                    )}
                  </Typography>
                </Divider>
              </Hidden>
              <Hidden smUp>
                <Divider sx={{ mt: 3, mb: 2 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", textAlign: "center" }}
                >
                  {t("Select maximum amount of downloads and authentication.")}
                </Typography>
              </Hidden>
            </Grid>
            <Hidden smDown>
              <Stack
                direction="row"
                divider={<Divider orientation="vertical" flexItem />}
                spacing={2}
                alignItems="center"
                justifyContent="center"
                width={"100%"}
                sx={{ ml: 2 }}
              >
                <TextField
                  value={maxDownloads}
                  margin="dense"
                  id="duration"
                  label={t("Maximum amount of downloads")}
                  type="number"
                  sx={{ width: "100%" }}
                  inputProps={{ min: 0, pattern: "([^0-9]*)" }}
                  variant="outlined"
                  onChange={handleMaximumDownloads}
                  error={maxDownloadsError}
                />
                <ToggleButtonGroup
                  color="primary"
                  value={auth}
                  exclusive
                  onChange={handleAuthentication}
                  sx={{
                    width: "100%",
                    justifyContent: "center",
                    height: "80%",
                  }}
                >
                  <ToggleButton value={true}>
                    {t("Authentication required")}
                  </ToggleButton>
                  <ToggleButton value={false}>
                    {t("Authentication not required")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Hidden>
            <Hidden smUp>
              <Grid item xs={12}>
                <TextField
                  value={maxDownloads}
                  margin="dense"
                  id="duration"
                  label={t("Maximum amount of downloads")}
                  type="number"
                  sx={{ width: "100%" }}
                  inputProps={{ min: 0, pattern: "([^0-9]*)" }}
                  variant="outlined"
                  onChange={handleMaximumDownloads}
                  error={maxDownloadsError}
                />
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  color="primary"
                  value={auth}
                  exclusive
                  onChange={handleAuthentication}
                  sx={{
                    width: "100%",
                    justifyContent: "center",
                    height: "80%",
                  }}
                >
                  <ToggleButton value={true}>
                    {t("Authentication required")}
                  </ToggleButton>
                  <ToggleButton value={false}>
                    {t("Authentication not required")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Hidden>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button onClick={handleSubmit} disabled={!isConfirmButtonEnabled}>
            {t("Submit")}
          </Button>
        </DialogActions>
      </Dialog>
      <LinkSummaryDialog
        open={openGeneratedLinkSummary}
        setOpen={setOpenGeneratedLinkSummary}
        generatedLinkText={generatedLinkText}
        generatedLink={generatedLink}
        name={props.name}
      />
    </Box>
  );
}
